import { useRuntimeConfig } from 'nuxt/app';
import type { MultilinkStoryblok } from '../types/storyblok';

/**
 * Adds a trailing and leading slash where applicable.
 */
export function normalizeUrl(url: string): string {
  if (!url) {
    return url;
  }

  if (url.startsWith('mailto:')) {
    // Ignore mailto links
    return url;
  }

  const hasFileEnding = /\.[a-z0-9]{2,4}$/i.test(url);
  if (!url.startsWith('http') && hasFileEnding === false) {
    // This is not an external link and not a file

    let pathParts = url.split('/').filter(entry => entry !== '');

    // Remove the Storyblok path prefix, if it is set
    const config = useRuntimeConfig();
    if (config.public.STORYBLOK_PATH_PREFIX) {
      pathParts = pathParts.filter(entry => entry !== config.public.STORYBLOK_PATH_PREFIX);
    }

    if (pathParts.length === 1 && pathParts[0].startsWith('#')) {
      // This is a hash link, leave it as-is
      url = pathParts[0];
    } else {
      // Ensure the url starts and ends with a slash
      url = `/${pathParts.join('/')}/`;
    }
  }

  return url;
}

export function urlIsActive(currentUrl: string, url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  // Normalize current route and given url for comparison
  return normalizeUrl(currentUrl) === normalizeUrl(url);
}

export function urlExtractFromLink(link: MultilinkStoryblok) {
  if (link.linktype === 'story') {
    return link?.story?.full_slug ?? link?.cached_url;
  }
  if (link.linktype === 'asset') {
    return link?.url;
  }
  if (link.linktype === 'url') {
    return link?.url;
  }
}

export function urlIsExternal(url: string) {
  const isShopOrSiteDomain = /^https:\/\/(\w+\.wmf-coffeemachines\.com|\w+\.do-show\.y1\.de)/.test(url);
  if (isShopOrSiteDomain) {
    // Links pointing to the shop should not be considered external
    return false;
  }

  return /^https?:\/\//.test(url);
}

export function urlIsShowroom(link: MultilinkStoryblok) {
  const url = urlExtractFromLink(link);
  return isShowroomUrl(url ?? '');
}

export function isShowroomUrl(url: string) {
  return /\/?(de_de\/produkte|de_at\/produkte|de_ch\/produkte|en_com\/products)\/?(\?.*)?$/.test(url);
}
